/**
 * FormField Component
 * Renders a single form field based on its type and configuration
 * Supports conditional disabling (grayed out) via conditionallyDisabled prop
 */

import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { FormField as FormFieldType } from '../../types/form';

interface FormFieldProps {
  field: FormFieldType;
  repeatIndex?: number;
  disabled?: boolean;
  conditionallyDisabled?: boolean;
  showWarning?: boolean;
}

export const FormField: React.FC<FormFieldProps> = ({
  field,
  repeatIndex,
  disabled = false,
  conditionallyDisabled = false,
  showWarning = false,
}) => {
  const { register, control, watch, formState: { errors } } = useFormContext();

  const fieldName = repeatIndex !== undefined
    ? `${field.fieldId}_${repeatIndex}`
    : field.fieldId;

  const error = errors[fieldName];
  const isRequired = field.validation?.required;

  // Field is effectively disabled if globally disabled, readonly, or conditionally disabled
  const isDisabled = disabled || field.readonly || conditionallyDisabled;

  // Base input classes
  const inputClasses = `
    w-full px-3 py-2 border rounded-md
    ${error ? 'border-red-500' : 'border-gray-300'}
    ${isDisabled ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'bg-white'}
    focus:outline-none focus:ring-2 focus:ring-blue-500
  `;

  const labelClasses = `block text-sm font-medium mb-1 ${
    conditionallyDisabled ? 'text-gray-400' : 'text-gray-700'
  }`;

  // Determine grid column span based on layout width
  const colSpanClass = field.layout?.width === 'full' ? 'md:col-span-2' : '';

  // Wrapper classes for the entire field when conditionally disabled
  const wrapperClasses = `mb-4 ${colSpanClass} ${conditionallyDisabled ? 'opacity-50' : ''}`;

  // Tailwind grid column classes (can't use dynamic template strings)
  const gridColsMap: Record<number, string> = {
    2: 'grid grid-cols-2 gap-2',
    3: 'grid grid-cols-3 gap-2',
    4: 'grid grid-cols-4 gap-2',
    6: 'grid grid-cols-6 gap-2',
  };

  // Render based on field type
  const renderField = () => {
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            {...register(fieldName, {
              required: isRequired && !conditionallyDisabled && 'This field is required',
              maxLength: field.validation?.maxLength && {
                value: field.validation.maxLength,
                message: `Maximum ${field.validation.maxLength} characters`,
              },
              pattern: field.validation?.pattern ? {
                value: new RegExp(field.validation.pattern),
                message: 'Invalid format',
              } : undefined,
            })}
            placeholder={field.placeholder}
            disabled={isDisabled}
            className={inputClasses}
          />
        );

      case 'textarea':
        return (
          <textarea
            {...register(fieldName, {
              required: isRequired && !conditionallyDisabled && 'This field is required',
              maxLength: field.validation?.maxLength && {
                value: field.validation.maxLength,
                message: `Maximum ${field.validation.maxLength} characters`,
              },
            })}
            placeholder={field.placeholder}
            disabled={isDisabled}
            rows={4}
            className={inputClasses}
          />
        );

      case 'number':
      case 'integer':
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              step={field.type === 'integer' ? '1' : (field.validation?.precision ? Math.pow(10, -field.validation.precision) : '0.01')}
              {...register(fieldName, {
                required: isRequired && !conditionallyDisabled && 'This field is required',
                min: field.validation?.min !== undefined ? {
                  value: field.validation.min,
                  message: `Minimum value is ${field.validation.min}`,
                } : undefined,
                max: field.validation?.max !== undefined ? {
                  value: field.validation.max,
                  message: `Maximum value is ${field.validation.max}`,
                } : undefined,
                valueAsNumber: true,
              })}
              disabled={isDisabled}
              className={inputClasses}
            />
            {field.unit && <span className={`text-sm ${conditionallyDisabled ? 'text-gray-300' : 'text-gray-500'}`}>{field.unit}</span>}
          </div>
        );

      case 'date':
        // Resolve dynamic date bounds (e.g. "today" -> current date string)
        const resolveDateBound = (bound?: string): string | undefined => {
          if (!bound) return undefined;
          if (bound === 'today') return new Date().toISOString().split('T')[0];
          return bound; // Already a date string
        };
        const dateMax = resolveDateBound(field.validation?.dateMax);
        const dateMin = resolveDateBound(field.validation?.dateMin);
        // Always warn on future dates for date fields, even without explicit dateMax
        const watchedDate = watch(fieldName);
        const todayStr = new Date().toISOString().split('T')[0];
        const isFutureDate = watchedDate && watchedDate > todayStr;

        return (
          <>
            <input
              type="date"
              {...register(fieldName, {
                required: isRequired && !conditionallyDisabled && 'This field is required',
                validate: (value: string) => {
                  if (!value) return true;
                  const today = new Date().toISOString().split('T')[0];
                  if (dateMax) {
                    const max = field.validation?.dateMax === 'today' ? today : dateMax;
                    if (value > max!) return 'Date cannot be in the future';
                  }
                  if (value > today) return 'Warning: This date is in the future';
                  return true;
                },
              })}
              max={dateMax || todayStr}
              min={dateMin}
              disabled={isDisabled}
              className={`${inputClasses} ${isFutureDate ? 'border-amber-500 bg-amber-50' : ''}`}
            />
            {isFutureDate && (
              <div className="mt-1 p-2 bg-amber-50 border border-amber-400 rounded text-sm text-amber-800">
                ⚠️ This date is in the future. Please confirm this is correct.
              </div>
            )}
          </>
        );

      case 'time':
        return (
          <input
            type="time"
            {...register(fieldName, {
              required: isRequired && !conditionallyDisabled && 'This field is required',
            })}
            disabled={isDisabled}
            className={inputClasses}
          />
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            {...register(fieldName, {
              required: isRequired && !conditionallyDisabled && 'This field is required',
            })}
            disabled={isDisabled}
            className={inputClasses}
          />
        );

      case 'radio':
      case 'yesno':
      case 'yesnounknown':
        const radioOptions = field.type === 'yesno'
          ? [{ value: 'Y', label: 'Yes' }, { value: 'N', label: 'No' }]
          : field.type === 'yesnounknown'
          ? [{ value: 'Y', label: 'Yes' }, { value: 'N', label: 'No' }, { value: 'U', label: 'Unknown' }]
          : field.options || [];

        // Determine layout: inline for short option lists, grid for columns, vertical default
        const radioLayout = field.columns && gridColsMap[field.columns]
          ? gridColsMap[field.columns]
          : field.optionLayout === 'inline'
          ? 'flex flex-wrap gap-x-6 gap-y-2'
          : 'space-y-2';

        return (
          <div className={radioLayout}>
            {radioOptions.map((option) => (
              <label
                key={String(option.value)}
                className={`flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <input
                  type="radio"
                  value={String(option.value)}
                  {...register(fieldName, {
                    required: isRequired && !conditionallyDisabled && 'Please select an option',
                  })}
                  disabled={isDisabled}
                  className={`h-4 w-4 shrink-0 focus:ring-blue-500 ${isDisabled ? 'text-gray-300' : 'text-blue-600'}`}
                />
                <span className={`text-sm ${conditionallyDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        // Determine layout: grid for columns, inline, or vertical default
        const checkboxLayout = field.columns && gridColsMap[field.columns]
          ? gridColsMap[field.columns]
          : field.optionLayout === 'inline'
          ? 'flex flex-wrap gap-x-6 gap-y-2'
          : 'space-y-2';

        return (
          <div className={checkboxLayout}>
            {field.options?.map((option) => (
              <label
                key={String(option.value)}
                className={`flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <input
                  type="checkbox"
                  value={String(option.value)}
                  {...register(fieldName)}
                  disabled={isDisabled}
                  className={`h-4 w-4 shrink-0 rounded focus:ring-blue-500 ${isDisabled ? 'text-gray-300' : 'text-blue-600'}`}
                />
                <span className={`text-sm ${conditionallyDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );

      case 'dropdown':
        return (
          <select
            {...register(fieldName, {
              required: isRequired && !conditionallyDisabled && 'Please select an option',
            })}
            disabled={isDisabled}
            className={inputClasses}
          >
            <option value="">-- Select --</option>
            {field.options?.map((option) => (
              <option key={String(option.value)} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'calculated':
        return (
          <Controller
            name={fieldName}
            control={control}
            render={({ field: { value } }) => (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={value ?? ''}
                  readOnly
                  className={`${inputClasses} bg-gray-100 font-medium`}
                />
                {field.unit && <span className={`text-sm ${conditionallyDisabled ? 'text-gray-300' : 'text-gray-500'}`}>{field.unit}</span>}
              </div>
            )}
          />
        );

      case 'label':
      case 'header':
        return null; // Labels don't have inputs

      default:
        return (
          <input
            type="text"
            {...register(fieldName)}
            disabled={isDisabled}
            className={inputClasses}
          />
        );
    }
  };

  return (
    <div className={wrapperClasses}>
      {field.type !== 'label' && field.type !== 'header' && (
        <label htmlFor={fieldName} className={labelClasses}>
          {field.label}
          {isRequired && !conditionallyDisabled && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {field.type === 'header' && (
        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">{field.label}</h3>
      )}

      {field.type === 'label' && (
        <p className="text-sm text-gray-600">{field.label}</p>
      )}

      {renderField()}

      {showWarning && field.warningText && (
        <div className="mt-1 px-3 py-1.5 bg-red-50 border border-red-200 rounded text-sm text-red-700 font-medium">
          {field.warningText}
        </div>
      )}

      {field.helpText && (
        <p className={`mt-1 text-sm ${conditionallyDisabled ? 'text-gray-300' : 'text-gray-500'}`}>{field.helpText}</p>
      )}

      {error && !conditionallyDisabled && (
        <p className="mt-1 text-sm text-red-600">{error.message as string}</p>
      )}
    </div>
  );
};

export default FormField;
